import React from 'react';
import { MCPTool } from '@/types/mcpTool';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Box,
  ExternalLink,
  MoreVertical,
  Edit,
  Trash2,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Wrench,
} from 'lucide-react';

interface MCPToolCardProps {
  tool: MCPTool;
  onClick?: (tool: MCPTool) => void;
  onEdit?: (tool: MCPTool) => void;
  onDelete?: (tool: MCPTool) => void;
}

export const MCPToolCard: React.FC<MCPToolCardProps> = ({
  tool,
  onClick,
  onEdit,
  onDelete,
}) => {
  const handleCardClick = () => {
    onClick?.(tool);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(tool);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(tool);
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Auth badge styling
  const getAuthBadge = () => {
    if (!tool.auth_configured) {
      return (
        <Badge variant="outline" className="text-gray-600 bg-gray-50">
          <ShieldAlert className="h-3 w-3 mr-1" />
          No Auth
        </Badge>
      );
    }

    if (tool.requires_user_oauth) {
      return (
        <Badge variant="outline" className="text-purple-700 bg-purple-50 border-purple-200">
          <ShieldCheck className="h-3 w-3 mr-1" />
          OAuth
        </Badge>
      );
    }

    return (
      <Badge variant="outline" className="text-green-700 bg-green-50 border-green-200">
        <Shield className="h-3 w-3 mr-1" />
        {tool.auth_type === 'bearer' ? 'Bearer' : tool.auth_type}
      </Badge>
    );
  };

  return (
    <Card
      className="group relative p-5 hover:shadow-lg transition-all duration-200 cursor-pointer border border-slate-200 bg-white hover:border-blue-300"
      onClick={handleCardClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="p-2 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100">
            <Box className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate text-base">
              {tool.name}
            </h3>
            <p className="text-xs text-gray-500 truncate mt-0.5">
              {tool.base_url}
            </p>
          </div>
        </div>

        {/* Actions Menu */}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild onClick={handleMenuClick}>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleEdit}>
              <Edit className="h-4 w-4 mr-2" />
              {tool.managed ? 'Manage Connection' : 'Edit Tool'}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleCardClick}>
              <Wrench className="h-4 w-4 mr-2" />
              Manage Actions
            </DropdownMenuItem>
            {!tool.managed && <DropdownMenuItem
              onClick={handleDelete}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Tool
            </DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600 mb-4 line-clamp-2 min-h-[40px]">
        {tool.description}
      </p>

      {/* Footer Badges */}
      <div className="flex items-center gap-2 flex-wrap">
        {getAuthBadge()}

        <Badge variant="secondary" className="px-2 py-0.5">
          <Wrench className="h-3 w-3 mr-1" />
          {tool.actions?.length || 0} {tool.actions?.length === 1 ? 'Action' : 'Actions'}
        </Badge>

        {tool.base_url && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              window.open(tool.base_url, '_blank');
            }}
          >
            <ExternalLink className="h-3 w-3 mr-1" />
            View API
          </Button>
        )}
      </div>

      {/* Created date (subtle footer) */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <p className="text-xs text-gray-400">
          Created {tool.created_at && !Number.isNaN(Date.parse(tool.created_at)) ? new Date(tool.created_at).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          }) : '—'}
        </p>
      </div>
    </Card>
  );
};
